/**
 * Sessions Page
 * View and manage active sessions (devices) for the current user
 * Users can see where they're logged in and revoke sessions from other devices
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, Monitor, Laptop, Tablet } from 'lucide-react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/contexts/ToastContext';
import { useDialog } from '@/contexts/DialogContext';
import { sessionApi } from '@/services/api';
import type { Session } from '@/services/api/auth';
import { parseDevice, formatRelativeTime } from '@/utils/deviceParser';
import { getContainerStyle } from '@/styles/containers';

export const Sessions: React.FC = () => {
  usePageTitle('Sessions');
  const { isMobile } = useMediaQuery();
  const { success, error: showError } = useToast();
  const { confirm } = useDialog();

  // State
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [isRevokingAll, setIsRevokingAll] = useState(false);

  // Styles
  const containerStyle = getContainerStyle('medium', isMobile);

  const headerStyle: React.CSSProperties = {
    marginBottom: isMobile ? '1.5rem' : '2rem',
  };

  const headerTopStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'flex-start' : 'center',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? '1rem' : '0',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: isMobile ? '1.5rem' : '2rem',
    fontWeight: '700',
    color: 'var(--text-color)',
    marginBottom: '0.5rem',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: isMobile ? '0.875rem' : '1rem',
    color: 'var(--text-secondary)',
    marginBottom: '0',
  };

  const backLinkStyle: React.CSSProperties = {
    fontSize: isMobile ? '0.875rem' : '1rem',
    color: 'var(--primary-color)',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    marginBottom: '1rem',
  };

  const sectionStyle: React.CSSProperties = {
    backgroundColor: 'var(--surface-color)',
    padding: isMobile ? '1rem' : '1.5rem',
    borderRadius: 'var(--radius)',
    marginBottom: isMobile ? '1.5rem' : '2rem',
    boxShadow: 'var(--shadow)',
  };

  const sessionCardStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'flex-start' : 'center',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? '1rem' : '0',
    padding: '1rem',
    borderBottom: '1px solid var(--border-color)',
  };

  const sessionInfoStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    flex: 1,
  };

  const deviceIconStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    color: 'var(--text-secondary)',
  };

  const sessionDetailsStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  };

  const deviceNameStyle: React.CSSProperties = {
    fontSize: isMobile ? '0.9375rem' : '1rem',
    fontWeight: '600',
    color: 'var(--text-color)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const currentBadgeStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    fontWeight: '500',
    color: 'var(--success-color)',
    backgroundColor: 'var(--success-bg)',
    padding: '0.125rem 0.5rem',
    borderRadius: '9999px',
  };

  const sessionMetaStyle: React.CSSProperties = {
    fontSize: isMobile ? '0.8125rem' : '0.875rem',
    color: 'var(--text-secondary)',
  };

  const buttonStyle: React.CSSProperties = {
    padding: isMobile ? '0.5rem 0.75rem' : '0.5rem 1rem',
    fontSize: isMobile ? '0.8125rem' : '0.875rem',
    minHeight: '36px',
    backgroundColor: 'transparent',
    color: 'var(--error-color)',
    border: '1px solid var(--error-color)',
    borderRadius: 'var(--radius)',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  };

  const dangerButtonStyle: React.CSSProperties = {
    padding: isMobile ? '0.625rem 1rem' : '0.625rem 1.25rem',
    fontSize: isMobile ? '0.875rem' : '0.9375rem',
    minHeight: '36px',
    backgroundColor: 'var(--error-color)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  };

  const loadingStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '2rem',
    color: 'var(--text-secondary)',
  };

  const emptyStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '2rem',
    color: 'var(--text-secondary)',
  };

  // Fetch sessions on mount
  useEffect(() => {
    let cancelled = false;

    const fetchSessions = async () => {
      try {
        setIsLoading(true);
        const response = await sessionApi.getSessions();
        if (!cancelled && response.success) {
          setSessions(response.sessions);
        }
      } catch (err) {
        if (!cancelled) {
          showError(err instanceof Error ? err.message : 'Failed to load sessions');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchSessions();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRevokeSession = async (sessionId: string) => {
    const confirmed = await confirm({
      title: 'Log Out Device',
      message: 'Are you sure you want to log out this device? The session will be terminated immediately.',
      confirmText: 'Log Out',
      variant: 'danger',
    });
    if (!confirmed) return;

    setRevokingId(sessionId);
    try {
      const response = await sessionApi.revokeSession(sessionId);
      if (response.success) {
        success('Session revoked successfully');
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to revoke session');
    } finally {
      setRevokingId(null);
    }
  };

  const handleRevokeAllOther = async () => {
    const otherSessions = sessions.filter((s) => !s.isCurrent);
    if (otherSessions.length === 0) {
      showError('No other sessions to revoke');
      return;
    }

    const confirmed = await confirm({
      title: 'Log Out All Other Devices',
      message: `Are you sure you want to log out all ${otherSessions.length} other device${otherSessions.length === 1 ? '' : 's'}? This action cannot be undone.`,
      confirmText: 'Log Out All',
      variant: 'danger',
    });
    if (!confirmed) return;

    setIsRevokingAll(true);
    try {
      const response = await sessionApi.logoutAllDevices();
      if (response.success) {
        success('All other sessions have been revoked');
        // Keep only current session
        setSessions((prev) => prev.filter((s) => s.isCurrent));
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to revoke sessions');
    } finally {
      setIsRevokingAll(false);
    }
  };

  const getDeviceIcon = (deviceType: 'desktop' | 'mobile' | 'tablet' | 'unknown'): React.ReactNode => {
    switch (deviceType) {
      case 'mobile':
        return <Smartphone size={24} aria-hidden="true" />;
      case 'tablet':
        return <Tablet size={24} aria-hidden="true" />;
      case 'desktop':
        return <Monitor size={24} aria-hidden="true" />;
      default:
        return <Laptop size={24} aria-hidden="true" />;
    }
  };

  const otherSessionsCount = sessions.filter((s) => !s.isCurrent).length;

  return (
    <div style={containerStyle}>
      {/* Back Link */}
      <Link to="/profile" style={backLinkStyle}>
        &larr; Back to Profile
      </Link>

      {/* Header */}
      <div style={headerStyle}>
        <div style={headerTopStyle}>
          <div>
            <h1 style={titleStyle}>Active Sessions</h1>
            <p style={subtitleStyle}>Manage the devices where you're currently logged in</p>
          </div>
          {otherSessionsCount > 0 && (
            <button
              style={dangerButtonStyle}
              onClick={handleRevokeAllOther}
              disabled={isRevokingAll}
              onMouseEnter={(e) => {
                if (!isRevokingAll) {
                  e.currentTarget.style.opacity = '0.9';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              {isRevokingAll ? 'Logging out...' : `Log Out All Other Devices (${otherSessionsCount})`}
            </button>
          )}
        </div>
      </div>

      {/* Sessions List */}
      <div style={sectionStyle}>
        {isLoading ? (
          <div style={loadingStyle}>Loading sessions...</div>
        ) : sessions.length === 0 ? (
          <div style={emptyStyle}>No active sessions found</div>
        ) : (
          <div>
            {sessions.map((session, index) => {
              const device = parseDevice(session.userAgent);
              const isLast = index === sessions.length - 1;

              return (
                <div
                  key={session.id}
                  style={{
                    ...sessionCardStyle,
                    borderBottom: isLast ? 'none' : sessionCardStyle.borderBottom,
                  }}
                >
                  <div style={sessionInfoStyle}>
                    <div style={deviceIconStyle}>{getDeviceIcon(device.deviceType)}</div>
                    <div style={sessionDetailsStyle}>
                      <div style={deviceNameStyle}>
                        {device.displayName}
                        {session.isCurrent && <span style={currentBadgeStyle}>Current session</span>}
                      </div>
                      <div style={sessionMetaStyle}>
                        {session.ipAddress && <span>IP: {session.ipAddress} &middot; </span>}
                        Last active: {formatRelativeTime(session.lastUsedAt)}
                      </div>
                      <div style={sessionMetaStyle}>
                        Signed in:{' '}
                        {new Date(session.createdAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  </div>

                  {!session.isCurrent && (
                    <button
                      style={{
                        ...buttonStyle,
                        opacity: revokingId === session.id ? 0.7 : 1,
                        cursor: revokingId === session.id ? 'not-allowed' : 'pointer',
                        width: isMobile ? '100%' : 'auto',
                      }}
                      onClick={() => handleRevokeSession(session.id)}
                      disabled={revokingId === session.id}
                      onMouseEnter={(e) => {
                        if (revokingId !== session.id) {
                          e.currentTarget.style.backgroundColor = 'var(--error-color)';
                          e.currentTarget.style.color = 'white';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--error-color)';
                      }}
                    >
                      {revokingId === session.id ? 'Revoking...' : 'Log Out'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Help Text */}
      <p
        style={{
          fontSize: isMobile ? '0.8125rem' : '0.875rem',
          color: 'var(--text-secondary)',
          textAlign: 'center',
        }}
      >
        If you don't recognize a session, log it out immediately and consider changing your password.
      </p>
    </div>
  );
};
